import React, { FC, ImgHTMLAttributes, useRef, useState } from "react";
import clsx from "classnames";
import { useStyles } from "./Image.style";
import { Skeleton } from "../Skeleton";

interface IImageProps {
  src: string;
  src2x?: string;
  src3x?: string;
  transformImg?: boolean;
  radius?: number | string;
  showloading?: boolean;
}

export const ImageComponent: FC<
  IImageProps & ImgHTMLAttributes<HTMLImageElement>
> = ({
  alt,
  src,
  src2x,
  src3x,
  radius,
  transformImg,
  showloading,
  ...otherProps
}) => {
  const ref = useRef<HTMLImageElement>(null);
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);

  const isExternalLink = src?.startsWith("https");
  const onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (ref.current) {
      if (ref.current.srcset !== src) {
        ref.current.srcset = src;
      } else if (otherProps.onError) {
        otherProps.onError(e);
      } else {
        ref.current.style.opacity = "0";
      }
    }
  };

  return (
    <>
      {isLoading && (isExternalLink || showloading) && (
        <Skeleton
          {...otherProps}
          data-testid="skeleton-image"
          style={{ ...otherProps.style, borderRadius: radius }}
          className={clsx(
            classes.root,
            { isHide: !isLoading },
            otherProps.className,
          )}
        />
      )}
      <img
        {...otherProps}
        className={clsx(
          classes.root,
          { isHide: isLoading },
          { transformImg },
          otherProps.className,
        )}
        srcSet={otherProps.srcSet || `${src} 1x, ${src2x} 2x, ${src3x} 3x`}
        style={{ borderRadius: radius }}
        ref={ref}
        src={src}
        alt={alt}
        onError={onError}
        onLoad={() => setIsLoading(false)}
      />
    </>
  );
};

export const Image = React.memo(ImageComponent);
